import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { AIMessageRole, AIAgentId } from './enum';
import { ActionButton } from './components/ActionButton';
import { ClarifyingQuestions } from './components/ClarifyingQuestions';

export type ChatMessage = {
  id: string;
  role: AIMessageRole
  text?: string;
  raw?: any;
  query?: string | null;
};

export function MessageList({ messages, onClickTag, loading, checkTagHasPdf, onSummarizePdf, onSuggestionClick }: {
  messages: ChatMessage[];
  onClickTag: (sid: string) => void;
  loading: boolean;
  checkTagHasPdf?: (tagId: string) => boolean;
  onSummarizePdf?: (tagId: string, tagTitle?: string) => Promise<void>;
  onSuggestionClick?: (suggestion: any) => void;
}) {
  return (
    <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: '15px' }}>
      {messages.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 16 }}>Ask anything about this tour. Example: "Show me 5 tags"</Typography>
      )}

      {messages.map((m) => {
        const isUser = m.role === AIMessageRole.User;
        const isAssistant = m.role === AIMessageRole.Bot;
        return (
          <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
            {isAssistant && (
              <Typography variant="caption" color="text.secondary">TAI</Typography>
            )}
            {m.text && !(isAssistant && m.raw?.agentId === AIAgentId.PdfAgent) && (
              isUser ? (
                <Box
                  sx={{
                    display: 'inline-block',
                    bgcolor: '#EAECF0',
                    color: 'text.primary',
                    borderRadius: '12px',
                    p: '15px',
                    maxWidth: '100%',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: 16 }}>{m.text}</Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontSize: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {m.text}
                </Typography>
              )
            )}

            {isAssistant && m.raw?.agentId === AIAgentId.DefaultAgent && Array.isArray(m.raw?.clarifyingQuestions) && m.raw.clarifyingQuestions.length > 0 && (
              <ClarifyingQuestions questions={m.raw.clarifyingQuestions} />
            )}

            {isUser && m.raw?.screenshot && (
              <Box sx={{ mt: 1 }}>
                <Box
                  component="img"
                  src={m.raw.screenshot}
                  alt="Screenshot"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    height: 'auto',
                    borderRadius: '12px',
                    border: '1px solid #EAECF0',
                    display: 'block',
                  }}
                />
              </Box>
            )}
            {isAssistant && m.raw?.agentId === AIAgentId.PdfAgent && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {m.raw?.response && (
                  <Paper
                    sx={{
                      p: 2,
                      border: '1px solid #EAECF0',
                      borderRadius: '12px',
                      backgroundColor: '#FAFAFA',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.raw.response}
                    </Typography>
                  </Paper>
                )}

                {Array.isArray(m.raw?.results) && m.raw.results.length > 0 && m.raw.results.map((r: any, idx: number) => {
                  const url = r?.url;
                  const summary = r?.summary;
                  const tag = r?.tag || {};
                  const sid = tag?.tagSid || tag?.sid;
                  return (
                    <Paper
                      key={sid || idx}
                      sx={{
                        p: 2,
                        border: '1px solid #EAECF0',
                        borderRadius: '12px',
                        backgroundColor: '#FAFAFA',
                      }}
                    >
                      {summary && (
                        <Typography variant="body2" sx={{ fontSize: 16, mb: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {summary}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {url && (
                          <ActionButton
                            text="Open PDF"
                            href={url}
                            color="#6B7280"
                          />
                        )}
                        {sid && (
                          <ActionButton
                            text={tag?.title || 'Go to tag'}
                            onClick={() => onClickTag(String(sid))}
                            color="#6B7280"
                          />
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
            {isAssistant && m.raw?.agentId !== AIAgentId.PdfAgent && Array.isArray(m.raw?.results) && m.raw.results.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {m.raw.results
                  .filter((t: any) => t && (t.sid || t.defaultSid) && t.title)
                  .map((t: any) => {
                    const sid = t.sid || t.defaultSid;
                    const hasPdf = checkTagHasPdf?.(String(sid)) || false;
                    return (
                      <Box key={sid} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <ActionButton
                          text={t.title}
                          onClick={() => onClickTag(String(sid))}
                          color="#6B7280"
                        />
                        {hasPdf && onSummarizePdf && (
                          <ActionButton
                            text="Summarize PDF"
                            onClick={() => onSummarizePdf?.(String(sid), t.title)}
                            color="#FF6B35"
                          />
                        )}
                      </Box>
                    );
                  })}
              </Box>
            )}

            {isAssistant && m.raw?.isPdfSummary && m.raw?.pdfUrl && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <ActionButton
                  text="Open PDF file"
                  href={m.raw.pdfUrl}
                  startIcon={<span style={{ fontSize: 14 }}>📄</span>}
                  color="#FF6B35"
                />
                {m.raw?.tagId && onClickTag && (
                  <ActionButton
                    text={`Go to tag ${m.raw.tagTitle || m.raw.tagId}`}
                    onClick={() => onClickTag(m.raw.tagId)}
                    startIcon={<span style={{ fontSize: 14 }}>🏷️</span>}
                    color="#4A90E2"
                  />
                )}
              </Box>
            )}

            {/* Render screenshot for assistant messages (both active chat and loaded conversations) */}
            {isAssistant && m.raw?.screenshot && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  component="img"
                  src={m.raw.screenshot}
                  alt="Screenshot Analysis"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    height: 'auto',
                    borderRadius: '12px',
                    border: '1px solid #EAECF0',
                    display: 'block',
                  }}
                />
              </Box>
            )}

            {isAssistant && m.raw?.isWelcome && Array.isArray(m.raw?.suggestions) && onSuggestionClick && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {m.raw.suggestions.map((suggestion: any, idx: number) => (
                  <ActionButton
                    key={idx}
                    text={suggestion.label}
                    onClick={() => onSuggestionClick(suggestion)}
                    color="#6B7280"
                    disabled={loading}
                  />
                ))}
              </Box>
            )}

            {isAssistant && m.raw?.isTagNavigation && m.raw?.tagSuggestion && onSuggestionClick && (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <ActionButton
                  text={m.raw.tagSuggestion.label}
                  onClick={() => onSuggestionClick(m.raw.tagSuggestion)}
                  startIcon={<span style={{ fontSize: 14 }}>ℹ️</span>}
                  color="#6B7280"
                  disabled={loading}
                />

                {m.raw.hasPdf && onSummarizePdf && (
                  <ActionButton
                    text="Summarize PDF"
                    onClick={() => onSummarizePdf?.(m.raw.tagId, m.raw.tagTitle)}
                    startIcon={<span style={{ fontSize: 14 }}>📄</span>}
                    color="#FF6B35"
                    disabled={loading}
                  />
                )}

                {m.raw.hasPdf && m.raw.pdfUrl && (
                  <ActionButton
                    text="Open PDF"
                    href={m.raw.pdfUrl}
                    startIcon={<span style={{ fontSize: 14 }}>🔗</span>}
                    color="#4CAF50"
                  />
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <span className="ai-dot-pulse" />
          <Typography variant="body2" sx={{ fontSize: 16 }}>Thinking…</Typography>
        </Box>
      )}
    </Box>
  );
}

